import { Box, IconButton } from '@mui/material';
import Image from 'next/image';
import { FC, PropsWithChildren } from 'react';

import {
  AbgTypography,
  AutocompleteOption,
  BookmarkLocationIcon,
  InfoIcon,
} from '@abg/kit-base';
import { LocationSuggestionTypes } from '@abg/shared/constants';

import { LocationIcon } from '../../atoms/location-icon/LocationIcon';
import {
  OptionWrapper,
  Properties as OptionProps,
} from '../../atoms/option-wrapper/OptionWrapper';
import { LocationSuggestionOption } from '../../location-search.types';

import {
  avisFirstSuggestionAvailabeText,
  avisFirstSuggestionOptionWrapper,
} from './suggestion-option.styling';
import * as styles from './suggestion-option.styling';
import { getLocationSubtitle } from './suggestion-option.utils';

import avisFirstLogo from '../../../../../assets/avis-first-logo.svg';

type Props = {
  suggestion: LocationSuggestionOption;
  onInfoClick?: (option: LocationSuggestionOption) => void;
  showInfoIcon?: boolean;
  avisFirstAvailableLabel?: string;
  avisFirstDeliveryPickupLabel?: string;
  isAFLocalMarketChangesEnabled?: boolean;
  isAFLocalMarketStepOneOtherLocationLabelEnabled?: boolean;
  // Hoisted from a per-option useMediaQuery: every rendered option used to open
  // its own media-query subscription, so a keystroke re-rendered the whole list
  // and re-ran N subscriptions. The parent computes it once and passes it down.
  isMobile: boolean;
  multipleLocationsLabel?: string;
} & OptionProps &
  PropsWithChildren;

type AvisFirstProps = {
  suggestion: LocationSuggestionOption;
  avisFirstAvailableLabel?: string;
  isAFLocalMarketStepOneOtherLocationLabelEnabled?: boolean;
  isAFLocalMarketChangesEnabled?: boolean;
};

export const AvisFirstSuggestionOption: FC<AvisFirstProps> = ({
  suggestion,
  avisFirstAvailableLabel,
  isAFLocalMarketStepOneOtherLocationLabelEnabled,
  isAFLocalMarketChangesEnabled,
}) => {
  const avisFirstDeliveryLabelInRecent =
    isAFLocalMarketChangesEnabled &&
    suggestion.suggestionTypeCode === 'RECENT' &&
    !!suggestion.deliveryAddress;
  const isABG_AVIS_FIRST_DELIVERY =
    suggestion.suggestionTypeCode ===
    LocationSuggestionTypes.ABG_AVIS_FIRST_DELIVERY;

  return (
    <>
      {/* Avis First Logo below locations */}
      {suggestion.suggestionTypeCode !== 'CURRENT' &&
        suggestion?.isAvisFirstLocation &&
        (isAFLocalMarketStepOneOtherLocationLabelEnabled ||
          suggestion.suggestionTypeCode ===
            LocationSuggestionTypes.ABG_AVIS_FIRST_DELIVERY) && (
          <Box sx={avisFirstSuggestionOptionWrapper}>
            <Image
              data-testid="avis-first-logo-bookingwidget-location"
              style={{
                width: '58px',
                height: '8px',
              }}
              src={avisFirstLogo}
              alt="avis-first-logo"
              width={0}
              height={0}
            />
            {isABG_AVIS_FIRST_DELIVERY || avisFirstDeliveryLabelInRecent ? (
              <></>
            ) : (
              <AbgTypography
                variant="caption"
                sx={avisFirstSuggestionAvailabeText}
              >
                {avisFirstAvailableLabel}
              </AbgTypography>
            )}
          </Box>
        )}
    </>
  );
};

export const SuggestionOption: FC<Props> = ({
  suggestion,
  onInfoClick,
  showInfoIcon = false,
  optionTestIdBuilder,
  option,
  avisFirstAvailableLabel,
  avisFirstDeliveryPickupLabel,
  isAFLocalMarketChangesEnabled,
  isAFLocalMarketStepOneOtherLocationLabelEnabled,
  isMobile,
  multipleLocationsLabel,
  ...optionProperties
}) => {
  const shouldShowInfoIcon =
    showInfoIcon &&
    isMobile &&
    onInfoClick &&
    suggestion.suggestionTypeCode !== 'CURRENT';

  const handleInfoClick = (event: React.MouseEvent) => {
    event.stopPropagation();

    if (
      suggestion.suggestionTypeCode ===
      LocationSuggestionTypes.ABG_AVIS_FIRST_DELIVERY
    ) {
      return;
    }

    if (
      suggestion.suggestionTypeCode === 'RECENT' &&
      suggestion.deliveryAddress
    ) {
      return;
    }

    onInfoClick?.(suggestion);
  };

  if (shouldShowInfoIcon) {
    return (
      <Box sx={styles.mainContainerStyle} component="li">
        <Box sx={styles.flexOneStyle}>
          <OptionWrapper
            {...optionProperties}
            optionTestIdBuilder={optionTestIdBuilder}
            option={option}
            component="div"
          >
            <AutocompleteOption
              iconElement={
                <LocationIcon location={suggestion} mode="list-option" />
              }
              title={suggestion.locationName}
              subTitle={getLocationSubtitle(suggestion, multipleLocationsLabel)}
              testIdPrefix="location-search"
              iconSx={styles.suggestionIconStyle}
              AvisFirstSuggestionOption={
                <AvisFirstSuggestionOption
                  suggestion={suggestion}
                  avisFirstAvailableLabel={avisFirstAvailableLabel}
                  isAFLocalMarketStepOneOtherLocationLabelEnabled={
                    isAFLocalMarketStepOneOtherLocationLabelEnabled
                  }
                  isAFLocalMarketChangesEnabled={isAFLocalMarketChangesEnabled}
                />
              }
              ABG_AVIS_FIRST_DELIVERY={
                LocationSuggestionTypes.ABG_AVIS_FIRST_DELIVERY
              }
              avisFirstDeliveryPickupLabel={avisFirstDeliveryPickupLabel}
              suggestionTypeCode={suggestion.suggestionTypeCode}
              isAFLocalMarketChangesEnabled={isAFLocalMarketChangesEnabled}
              isAFLocalMarketStepOneOtherLocationLabelEnabled={
                isAFLocalMarketStepOneOtherLocationLabelEnabled
              }
              deliveryAddress={
                suggestion?.deliveryAddress as string | undefined
              }
            />
          </OptionWrapper>
        </Box>
        <Box sx={styles.iconColumnStyle}>
          {suggestion.suggestionGroup === 'SAVED' && (
            <IconButton
              size="small"
              sx={styles.bookmarkIconButtonStyle}
              data-testid="location-search-option-bookmark-button"
            >
              <BookmarkLocationIcon sx={{ fontSize: 16 }} />
            </IconButton>
          )}
          <IconButton
            onClick={handleInfoClick}
            size="small"
            sx={styles.infoIconButtonStyle}
            data-testid="location-search-option-info-button"
            aria-label="View location details"
          >
            <InfoIcon
              viewBox="0 0 12 13"
              color="inherit"
              sx={{ fontSize: 16 }}
            />
          </IconButton>
        </Box>
      </Box>
    );
  }

  return (
    <OptionWrapper
      {...optionProperties}
      optionTestIdBuilder={optionTestIdBuilder}
      option={option}
    >
      <AutocompleteOption
        iconElement={<LocationIcon location={suggestion} mode="list-option" />}
        title={suggestion.locationName}
        postIcon={
          suggestion.suggestionGroup === 'SAVED'
            ? BookmarkLocationIcon
            : undefined
        }
        subTitle={getLocationSubtitle(suggestion, multipleLocationsLabel)}
        AvisFirstSuggestionOption={
          <AvisFirstSuggestionOption
            suggestion={suggestion}
            avisFirstAvailableLabel={avisFirstAvailableLabel}
            isAFLocalMarketStepOneOtherLocationLabelEnabled={
              isAFLocalMarketStepOneOtherLocationLabelEnabled
            }
            isAFLocalMarketChangesEnabled={isAFLocalMarketChangesEnabled}
          />
        }
        ABG_AVIS_FIRST_DELIVERY={
          LocationSuggestionTypes.ABG_AVIS_FIRST_DELIVERY
        }
        avisFirstDeliveryPickupLabel={avisFirstDeliveryPickupLabel}
        suggestionTypeCode={suggestion.suggestionTypeCode}
        testIdPrefix="location-search"
        iconSx={styles.suggestionIconStyle}
        isAFLocalMarketChangesEnabled={isAFLocalMarketChangesEnabled}
        isAFLocalMarketStepOneOtherLocationLabelEnabled={
          isAFLocalMarketStepOneOtherLocationLabelEnabled
        }
        deliveryAddress={suggestion.deliveryAddress as string | undefined}
      />
    </OptionWrapper>
  );
};
